import {
  HTMLProps,
  useCallback,
  useMemo,
} from 'react';

import {
  LocalVideoTrack,
  RemoteUser,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
} from 'agora-rtc-react';

import MicrophoneIndicator from '@/app/components/MicrophoneIndicator';
import NoVideoIndicator from '@/app/components/NoVideoIndicator';
import VideoDisplayTitle from '@/app/components/VideoDisplayTitle';
import { useParticipantData } from '@/app/queries/MeetingQuery/context';
import { cn } from '@/lib/utils';
import { useAppContext } from '../AppContext';

export interface VideoTrackInfo {
  uid: number,
  source: ICameraVideoTrack | IAgoraRTCRemoteUser,
  sourceType: 'local' | 'remote',
  isEnlarged: boolean,
  hasAudio: boolean,
  hasVideo: boolean,
}

export type VideoDisplayVariant = 'stage' | 'tile';

// On a phone the stage runs edge to edge under the floating header, so its
// microphone badge is pulled clear of it and the name is dropped — a
// full-screen view needs no label. On a desktop the stage is a panel of its
// own and the name sits in its corner on a chip.
//
// A tile is too small for a chip: rounded corners inside rounded corners fight
// each other. Its name rides a scrim across the bottom edge instead.
const BADGE_PLACEMENT: Record<VideoDisplayVariant, { title: string, microphone: string }> = {
  stage: {
    title: 'hidden md:bottom-0 md:left-0 md:m-3 md:block md:max-w-xs md:rounded-xl md:bg-slate-700/50 md:p-2 md:text-sm',
    microphone: 'top-20 md:top-3',
  },
  tile: {
    title: 'inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 pb-1.5 pt-5 text-xs',
    microphone: '',
  },
};

interface VideoDisplayProps extends Omit<HTMLProps<HTMLDivElement>, 'onClick'> {
  videoTrack: VideoTrackInfo;
  // A tile takes the click on an overlay of its own, so that the whole tile
  // swaps and not only the part the player covers.
  onClick?(videoTrack: VideoTrackInfo): void;
  variant?: VideoDisplayVariant;
}

export default function VideoDisplay({
  onClick, videoTrack, variant = 'tile', ...divProps
}: VideoDisplayProps) {
  const { speaker, isMainVideoMirrored } = useAppContext();
  const participantData = useParticipantData(videoTrack.uid);

  const onClickTrack = useCallback(() => {
    onClick?.(videoTrack);
  }, [onClick, videoTrack]);

  // A remote stage — and a screen share at any size — is letterboxed:
  // cropping would cut away part of the property, or of the quote, being
  // shown. Everything else fills its box, the way a camera viewfinder does.
  //
  // The SDK is told to never mirror: its player only ever SETS the mirroring
  // transform on the <video> element it reuses and never clears it, so a
  // front→rear camera flip stayed mirrored. Mirroring is owned in CSS below,
  // on the video element alone — the badges on top must not flip with it.
  const isScreenShare = Boolean(participantData?.isScreenShare);
  const playerConfig = useMemo(() => ({
    fit: isScreenShare || (variant === 'stage' && videoTrack.sourceType === 'remote')
      ? 'contain' as const
      : 'cover' as const,
    mirror: false,
  }), [variant, videoTrack.sourceType, isScreenShare]);

  const isMirrored = videoTrack.sourceType === 'local' && isMainVideoMirrored;

  const badges = BADGE_PLACEMENT[variant];

  switch (videoTrack.sourceType) {
    case 'local':
      return (
        <>
          <VideoDisplayTitle className={badges.title} title={participantData?.name} />
          <LocalVideoTrack
            {...divProps}
            className={cn(divProps.className, { '[&_video]:-scale-x-100': isMirrored })}
            track={videoTrack.source as ICameraVideoTrack}
            play={videoTrack.hasVideo}
            videoPlayerConfig={playerConfig}
            onClick={onClickTrack}
          >
            <MicrophoneIndicator className={badges.microphone} isMicrophoneOn={videoTrack.hasAudio} />
            <NoVideoIndicator isCameraOn={videoTrack.hasVideo} />
          </LocalVideoTrack>
        </>

      )
    case 'remote':
      return (
        <>
          {!participantData?.isScreenShare && (
            <VideoDisplayTitle className={badges.title} title={participantData?.name} />
          )}
          <RemoteUser
            {...divProps}
            playbackDeviceId={speaker.deviceId}
            user={videoTrack.source as IAgoraRTCRemoteUser}
            playVideo={true}
            playAudio={true}
            videoPlayerConfig={playerConfig}
            onClick={onClickTrack}
          >
            <MicrophoneIndicator className={badges.microphone} isMicrophoneOn={videoTrack.hasAudio} />
            <NoVideoIndicator isCameraOn={videoTrack.hasVideo} />
          </RemoteUser>
        </>
      );
    default: {
      const neverType: never = videoTrack.sourceType;
      throw new TypeError(`Unhandled video type: ${neverType}`);
    }
  }
}
